import { getHdBodygraphStream } from '@/app/human-design/chart/bodygraph/hd-bodygraph-stream-metadata';
import { findHdBodygraphChannelByGates } from '@/app/human-design/chart/bodygraph/hd-bodygraph-metadata';

/** Public asset folder for the exported Figma stream visuals. */
export const HD_STREAMS_CHEAT_SHEET_IMAGE_BASE = 'assets/human-design/streams';

/**
 * Display order on the cheat sheet (matches the Figma sheets). The first
 * {@link HD_STREAMS_CHEAT_SHEET_SPLIT_INDEX} streams form print sheet 1, the rest sheet 2.
 */
export const HD_STREAMS_CHEAT_SHEET_ORDER: readonly string[] = [
  'gewaarworden', 'weten', 'begrijpen', 'smaak', 'gevoelens',
  'intuitie', 'emotie', 'instinct', 'gevoeligheid',
];
export const HD_STREAMS_CHEAT_SHEET_SPLIT_INDEX = 5;

// Numerieke labels (1..4); de weergave als gekleurd bolletje met wit cijfer
// gebeurt in de SCSS. Op deze manier is het cijfer bruikbaar voor screen-readers
// en blijft de kleur inline (per-stap) instelbaar via `markerColor`.
const STREAM_FLOW_MARKERS = ['1', '2', '3', '4'] as const;
/** Vaste kleuren van de vier stroom-stappen (❶…❹), identiek aan de Figma cheat sheet. */
const STREAM_FLOW_MARKER_COLORS = ['#d93c3c', '#1983bf', '#000000', '#eb9c26'] as const;
const STREAM_FLOW_LENGTH = 4;

/** Legend circuit colors — kept in sync with HD_BODYGRAPH_CIRCUITS / stream metadata. */
const LEGEND_CIRCUIT_COLORS: Readonly<Record<string, string>> = {
  'individueel': '#00C165',
  'collectief-logisch': '#05A1F9',
  'collectief-abstract': '#416CAC',
  'tribaal': '#F01C1C',
};

export interface HdStreamCheatSheetGateBlock {
  gate: number;
  title: string;
  desc: string;
  spectrum: string;
}

export interface HdStreamCheatSheetChannel {
  key: string;
  gateA: number;
  gateB: number;
  title: string;
  circuit: string;
  english: string;
  type: string;
  image: string;
  gateBlocks: HdStreamCheatSheetGateBlock[];
}

export interface HdStreamCheatSheetFlowStep {
  marker: string;
  /** Vaste stap-kleur van de marker (❶ rood, ❷ blauw, ❸ zwart, ❹ oranje). */
  markerColor: string;
  role: string;
  gate: number;
  name: string;
  note: string;
  unrest: string;
}

export interface HdStreamCheatSheetRow {
  key: string;
  /** 1 or 2 — which print sheet this stream belongs to. */
  sheet: 1 | 2;
  streamName: string;
  awarenessName: string;
  awarenessSubtitle: string;
  streamLabel: string;
  /** Label voor de "onrust/angst/nervositeit"-regels, afhankelijk van het bewustzijnscentrum. */
  unrestLabel: string;
  familyColor: string;
  flowImage: string;
  flowChannels: string;
  flowNote: string;
  flow: HdStreamCheatSheetFlowStep[];
  channels: HdStreamCheatSheetChannel[];
}

export interface HdStreamsCheatSheetLegendCircuit {
  label: string;
  color: string;
}

export interface HdStreamsCheatSheetLegend {
  title: string;
  intro: string;
  centersText: string;
  markersTitle: string;
  markers: { marker: string; role: string; color: string }[];
  circuitsTitle: string;
  circuitsIntro: string;
  circuits: HdStreamsCheatSheetLegendCircuit[];
  flowTitle: string;
  flow: string;
}

export interface HdStreamsCheatSheet {
  pageTitle: string;
  pageSubtitle: string;
  rows: HdStreamCheatSheetRow[];
  legend: HdStreamsCheatSheetLegend;
}

/**
 * Reader voor per-poort velden uit de `gates`-scope (streamFunction / fearKeynote).
 * Gescheiden van de streams-reader zodat de poort-functie en angst-keynote maar op
 * één plek onderhouden worden (`human-design/gates/{lang}.json`).
 */
export type HdStreamsCheatSheetGateFieldReader =
  (gate: number, field: 'streamFunction' | 'fearKeynote') => string;

/**
 * Assembles the full stromen-cheat-sheet view-model from the `streams` Transloco
 * scope. `read(key)` must return the (possibly empty) flat-key value for the active
 * content language — the page supplies a reader with NL fallback so untranslated EN
 * fields gracefully show the Dutch source. `readGateField` levert per poort de
 * stream-functie en angst-keynote uit de `gates`-scope.
 */
export function buildHdStreamsCheatSheet(
  read: (key: string) => string,
  readGateField: HdStreamsCheatSheetGateFieldReader,
): HdStreamsCheatSheet {
  const streamLabel = read('streamLabel');

  const rows = HD_STREAMS_CHEAT_SHEET_ORDER
    .map((streamKey, index) => buildRow(streamKey, index, streamLabel, read, readGateField))
    .filter((row): row is HdStreamCheatSheetRow => row !== null);

  return {
    pageTitle: read('pageTitle'),
    pageSubtitle: read('pageSubtitle'),
    rows,
    legend: buildLegend(read),
  };
}

function buildRow(
  streamKey: string,
  index: number,
  streamLabel: string,
  read: (key: string) => string,
  readGateField: HdStreamsCheatSheetGateFieldReader,
): HdStreamCheatSheetRow | null {
  const stream = getHdBodygraphStream(streamKey);
  if (!stream) {
    return null;
  }

  const base = `cheatSheet.${streamKey}`;

  const flow: HdStreamCheatSheetFlowStep[] = [];
  for (let i = 0; i < STREAM_FLOW_LENGTH; i++) {
    const gate = Number(read(`${base}.flow.${i}.gate`)) || 0;
    flow.push({
      marker: STREAM_FLOW_MARKERS[i],
      markerColor: STREAM_FLOW_MARKER_COLORS[i],
      role: read(`flowRoles.${i + 1}`),
      gate,
      name: read(`${base}.flow.${i}.name`),
      note: readGateField(gate, 'streamFunction'),
      unrest: readGateField(gate, 'fearKeynote'),
    });
  }

  const channels = [
    buildChannel(streamKey, flow[0]?.gate, flow[1]?.gate, read),
    buildChannel(streamKey, flow[2]?.gate, flow[3]?.gate, read),
  ].filter((channel): channel is HdStreamCheatSheetChannel => channel !== null);

  const awarenessName = read(`awareness.${stream.awareness}.name`);
  const awarenessSubtitle = read(`awareness.${stream.awareness}.subtitle`);

  return {
    key: streamKey,
    sheet: index < HD_STREAMS_CHEAT_SHEET_SPLIT_INDEX ? 1 : 2,
    streamName: read(`streams.${streamKey}.name`),
    awarenessName,
    awarenessSubtitle,
    streamLabel,
    unrestLabel: read(`unrestLabels.${stream.awareness}`),
    familyColor: stream.color,
    flowImage: `${HD_STREAMS_CHEAT_SHEET_IMAGE_BASE}/${streamKey}-flow.png`,
    flowChannels: buildFlowChannelsLabel(flow),
    flowNote: read(`${base}.flowNote`),
    flow,
    channels,
  };
}

function buildChannel(
  streamKey: string,
  gateA: number | undefined,
  gateB: number | undefined,
  read: (key: string) => string,
): HdStreamCheatSheetChannel | null {
  if (gateA == null || gateB == null) {
    return null;
  }
  const channel = findHdBodygraphChannelByGates(gateA, gateB);
  if (!channel) {
    return null;
  }

  const base = `cheatSheet.${streamKey}.channels.${channel.key}`;

  const gateBlocks = [gateA, gateB].map(gate => ({
    gate,
    title: read(`${base}.gates.${gate}.title`),
    desc: read(`${base}.gates.${gate}.desc`),
    spectrum: read(`${base}.gates.${gate}.spectrum`),
  }));

  return {
    key: channel.key,
    gateA,
    gateB,
    title: read(`${base}.title`),
    circuit: read(`${base}.circuit`),
    english: read(`${base}.english`),
    type: read(`${base}.type`),
    image: `${HD_STREAMS_CHEAT_SHEET_IMAGE_BASE}/${streamKey}-${channel.key}.png`,
    gateBlocks,
  };
}

function buildFlowChannelsLabel(flow: HdStreamCheatSheetFlowStep[]): string {
  if (flow.length < STREAM_FLOW_LENGTH) {
    return '';
  }
  return `${flow[0].gate}-${flow[1].gate} → ${flow[2].gate}-${flow[3].gate}`;
}

function buildLegend(read: (key: string) => string): HdStreamsCheatSheetLegend {
  const markers = STREAM_FLOW_MARKERS.map((marker, i) => ({
    marker,
    role: read(`flowRoles.${i + 1}`),
    color: STREAM_FLOW_MARKER_COLORS[i],
  }));

  // Volgorde zoals in de Figma-legende.
  const circuitKeys = ['tribaal', 'individueel', 'collectief-logisch', 'collectief-abstract'];
  const circuits = circuitKeys.map(key => ({
    label: read(`legend.circuits.${key}`),
    color: LEGEND_CIRCUIT_COLORS[key] ?? '#999999',
  }));

  return {
    title: read('legend.title'),
    intro: read('legend.intro'),
    centersText: read('legend.centersText'),
    markersTitle: read('legend.markersTitle'),
    markers,
    circuitsTitle: read('legend.circuitsTitle'),
    circuitsIntro: read('legend.circuitsIntro'),
    circuits,
    flowTitle: read('legend.flowTitle'),
    flow: read('legend.flow'),
  };
}
